import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import styles from "./BannerAds.module.css";
import { Edit, EyeOff, Trash, Plus } from "lucide-react";
import Loading from "../../../common/HousGlass Loader/Loading";
import { useAdvertisements, useDeleteAdvertisement } from "../../../hooks/advertisment/useAdvertismentHooks";

// Separate component for the Edit icon
const EditIconComponent = ({ id, onEdit }) => {
  return (
    <Edit
      className={styles.icon}
      stroke="black"
      onClick={() => onEdit(id)}
      size={18}
    />
  );
};

// Separate component for the Hide icon
const HideIconComponent = ({ id, onHide }) => {
  return (
    <EyeOff
      className={styles.icon}
      stroke="black"
      onClick={() => onHide(id)}
      size={18}
    />
  );
};

// Separate component for the Delete icon
const DeleteIconComponent = ({ id, onDelete }) => {
  return (
    <Trash
      className={styles.icon}
      stroke="black"
      onClick={() => onDelete(id)}
      size={18}
    />
  );
};

// Confirmation Modal Component
const ConfirmationModal = ({ message, onConfirm, onCancel, isLoading }) => {
  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modalContent}>
        <p>{message}</p>
        <div className={styles.modalButtons}>
          <button 
            className={styles.modalButton} 
            onClick={onConfirm}
            disabled={isLoading}
          >
            {isLoading ? 'Deleting...' : 'Confirm'}
          </button>
          <button 
            className={styles.modalButton} 
            onClick={onCancel}
            disabled={isLoading}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};


const BannerAds = () => {
  const navigate = useNavigate();
  const { advertisementsData, isLoading: bannerLoading } = useAdvertisements();
  const { deleteAdvertisement, isLoading: isDeleteLoading } = useDeleteAdvertisement();
  console.log(advertisementsData,"data");
  
  
  // State to manage advertisements
  const [banners, setBanners] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 12;
  const [showModal, setShowModal] = useState(false);
  const [bannerToDelete, setBannerToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Effect to set banners when data is fetched
  useEffect(() => {
    if (advertisementsData && advertisementsData.advertisements && advertisementsData.advertisements.length > 0) {
      // Transform advertisement data to match existing banner structure
      const transformedBanners = advertisementsData.advertisements.map(ad => ({
        id: ad.id,
        title: ad.title,
        image_url: ad.image_url,
        link: ad.link,
        active: ad.active || true,
        description: ad.description,
        category: ad.category,
        start_date: ad.start_date,
        end_date: ad.end_date
      }));
      setBanners(transformedBanners);
      setIsLoading(false);
    } else {
      setIsLoading(false);
    }
  }, [advertisementsData]);

  const handleEdit = (id) => {
    navigate(`/admin/edit-banner/${id}`);
  };

  const handleHide = (id) => {
    // Toggle banner visibility (in a real app this would update the backend)
    setBanners(banners.map(banner => 
      banner.id === id ? {...banner, active: !banner.active} : banner
    ));
    console.log("Toggle visibility for banner with ID:", id);
  };

  // Opens the confirmation modal instead of deleting immediately
  const confirmDelete = (id) => {
    setBannerToDelete(id);
    setShowModal(true);
  };

  // Called when the user confirms deletion
  const confirmDeleteAction = async () => {
    if (bannerToDelete !== null) {
      try {
        setIsDeleting(true);
        await deleteAdvertisement(bannerToDelete);
        // After successful deletion, remove the banner from the state
        setBanners(banners.filter(banner => banner.id !== bannerToDelete));
        setShowModal(false);
        setBannerToDelete(null);
      } catch (error) {
        console.error('Delete failed:', error);
      } finally {
        setIsDeleting(false);
      }
    }
  };

  const cancelDelete = () => {
    setBannerToDelete(null);
    setShowModal(false);
  };
  if (isLoading || bannerLoading) {
    return <div className={styles.loading}><Loading/></div>;
  }

  if (!banners || banners.length === 0) {
    return (
      <div className={styles.empty}>
        <p>No banner ads found</p>
        <button 
          className={styles.addButton}
          onClick={() => navigate('/admin/add-banner')}
        >
          <Plus size={20} />
          Add Banner
        </button>
      </div>
    );
  }

  // Pagination logic
  const indexOfLastBanner = currentPage * itemsPerPage;
  const indexOfFirstBanner = indexOfLastBanner - itemsPerPage;
  const currentBanners = banners.slice(indexOfFirstBanner, indexOfLastBanner);
  const totalPages = Math.ceil(banners.length / itemsPerPage);

  const goToNextPage = () => {
    setCurrentPage((prev) => Math.min(prev + 1, totalPages));
  };

  const goToPrevPage = () => {
    setCurrentPage((prev) => Math.max(prev - 1, 1));
  };

  return (
    <div>
      <div className={styles.header}>
        <h1>Banner Ads Management</h1>
        <button 
          className={styles.addButton}
          onClick={() => navigate('/admin/add-banner')}
        >
          <Plus size={20} />
          Add Banner
        </button>
      </div>

      <div className={styles.bannersContainer}>
        {currentBanners.map((banner) => (
          <div key={banner.id} className={styles.bannerItem}>
            <img
              src={banner.image_url || "/images/placeholders/banner-placeholder.png"}
              alt={banner.title}
              className={styles.bannerImage}
            />
            <div className={styles.bannerDetails}>
              <h3>{banner.title}</h3>
              <p>{banner.description}</p>
            </div>
            <div className={styles.iconsOverlay}>
              <EditIconComponent id={banner.id} onEdit={handleEdit} />
              <HideIconComponent id={banner.id} onHide={handleHide} />
              <DeleteIconComponent id={banner.id} onDelete={confirmDelete} />
            </div>
          </div>
        ))}
      </div>

      {/* Pagination Controls */}
      <div className={styles.pagination}>
        <button
          className={styles.paginationButton}
          onClick={goToPrevPage}
          disabled={currentPage === 1}
        >
          Previous
        </button>
        <span className={styles.pageInfo}>
          Page {currentPage} of {totalPages} (Total: {advertisementsData.total} Banners)
        </span>
        <button
          className={styles.paginationButton}
          onClick={goToNextPage}
          disabled={currentPage === totalPages}
        >
          Next
        </button>
      </div>

      {/* Confirmation Modal */}
      {showModal && (
        <ConfirmationModal
          message={`Are you sure you want to delete "${banners.find(b => b.id === bannerToDelete)?.title}"?`}
          onConfirm={confirmDeleteAction}
          onCancel={cancelDelete}
          isLoading={isDeleting}
        />
      )}
    </div>
  );
};

export default BannerAds;